import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  TreePine, Crosshair, Wind, Thermometer, Eye, Gauge, Moon, Sun,
  ArrowLeft, Loader2, Clock, Activity, AlertTriangle, Droplets,
  Navigation, ChevronRight, CloudRain, Bird
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOCATION = { lat: 35.2098048, lon: -86.0794285 };

function RatingBadge({ rating }) {
  const colors = {
    "Excellent": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    "Very Good": "bg-green-500/20 text-green-400 border-green-500/30",
    "Good": "bg-lime-500/20 text-lime-400 border-lime-500/30",
    "Fair": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    "Poor": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${colors[rating] || colors["Fair"]}`}>
      {rating}
    </span>
  );
}

function ScoreRing({ score, label, color = "text-emerald-400", size = "text-3xl" }) {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  return (
    <div className="flex items-center gap-2">
      <span className={`${size} font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{score}</span>
      <span className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>/10</span>
    </div>
  );
}

function MoonIcon({ phase, illumination }) {
  const fill = `${illumination}%`;
  return (
    <div className="relative w-12 h-12">
      <Moon className="w-12 h-12 text-yellow-300" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-yellow-100">{illumination}%</span>
      </div>
    </div>
  );
}

export default function HuntingScoutingPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [searchParams] = useSearchParams();
  const defaultLocation = user?.preferences?.default_location || FALLBACK_LOCATION;
  const lat = parseFloat(searchParams.get("lat") || defaultLocation.lat || FALLBACK_LOCATION.lat);
  const lon = parseFloat(searchParams.get("lon") || defaultLocation.lon || FALLBACK_LOCATION.lon);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(null);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`);
        setIsPremium(res.data?.is_premium || res.data?.is_trial || user?.role === "admin");
      } catch {
        setIsPremium(user?.role === "admin");
      }
    };
    if (user) checkSubscription();
  }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/weather/hunting`, { params: { lat, lon } });
      setData(resp.data);
    } catch (err) {
      console.error("Hunting data fetch error:", err);
    } finally { setLoading(false); }
  }, [lat, lon]);

  useEffect(() => {
    fetchData();
  }, [lat, lon, fetchData]);

  if (isPremium === null) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-4xl mx-auto p-6">
          <Link to="/" className={`flex items-center gap-2 mb-8 ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="text-center py-20">
            <Crosshair className="w-16 h-16 mx-auto mb-4 text-amber-400 opacity-50" />
            <h2 className="text-2xl font-bold mb-2">Premium Feature</h2>
            <p className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Hunting & Scouting conditions are available for premium subscribers.</p>
            <Link to="/subscription" className="inline-block mt-4 px-6 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-500 transition-colors">Upgrade to Premium</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0e1a]' : 'bg-gray-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
      </div>
    );
  }

  if (!data?.success) return null;

  const { weather: wx, sun, moon, solunar, game_activity: ga, scent_control: scent, field_strategy: strategy, hunt_windows: huntWindows, comfort, safety } = data;

  return (
    <div data-testid="hunting-scouting-page" className={`min-h-screen pb-12 ${isDarkMode ? 'bg-[#0a0e1a] text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="hunting-back-btn" className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <button data-testid="hunting-refresh-button" onClick={fetchData} className={`text-xs px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'} transition-colors`}>Refresh</button>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
              <Crosshair className={`w-6 h-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Hunting & Scouting</h1>
              <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Conditions, game activity & solunar data</p>
            </div>
          </div>
        </div>

        {/* Weather Conditions Grid */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Current Conditions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2"><Thermometer className="w-4 h-4 text-red-400" /><span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Temperature</span></div>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{wx.temperature_f}°F</p>
              <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Feels like {wx.feels_like_f}°F</p>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2"><Wind className="w-4 h-4 text-slate-400" /><span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Wind</span></div>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{wx.wind_speed_mph} mph {wx.wind_direction}</p>
              {wx.wind_gust_mph && <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Gusts {wx.wind_gust_mph} mph</p>}
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2"><Gauge className="w-4 h-4 text-purple-400" /><span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Pressure</span></div>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{wx.pressure_hpa} hPa</p>
              <p className={`text-xs font-medium ${wx.pressure_trend === 'Falling' ? 'text-green-400' : wx.pressure_trend === 'Rising' ? 'text-blue-400' : (isDarkMode ? 'text-white/40' : 'text-gray-400')}`}>{wx.pressure_trend}</p>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
              <div className="flex items-center gap-2 mb-2"><Eye className="w-4 h-4 text-amber-400" /><span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Visibility</span></div>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{wx.visibility_mi} mi</p>
              <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>{wx.description}</p>
            </div>
          </div>
        </section>

        {/* Scent Control Alert */}
        <section className="mb-8">
          <div className={`p-4 rounded-2xl flex items-start gap-3 ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
            <Navigation className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" style={{ transform: `rotate(${wx.wind_direction_deg}deg)` }} />
            <div>
              <h3 className={`font-semibold text-sm mb-1 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Scent Control</h3>
              <p className={`text-sm ${isDarkMode ? 'text-amber-200/70' : 'text-amber-600'}`}>{scent.alert}</p>
            </div>
          </div>
        </section>

        {strategy && (
          <section className="mb-8" data-testid="hunting-strategy-detail-section">
            <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Field Strategy</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className={`p-5 rounded-2xl lg:col-span-2 ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`} data-testid="hunting-wind-strategy-detail">
                <div className="flex items-center gap-2 mb-3"><Navigation className="w-5 h-5 text-amber-400" /><h3 className="font-semibold">Wind & Access</h3></div>
                <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>{strategy.wind_strategy}</p>
                <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{strategy.thermal_note}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {strategy.stand_types?.map((stand) => (
                    <span key={stand} data-testid={`hunting-stand-type-${stand.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`text-xs px-2 py-1 rounded-lg ${isDarkMode ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>{stand}</span>
                  ))}
                </div>
              </div>
              <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`} data-testid="hunting-comfort-detail">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">Comfort</h3>
                  <span className={`font-black ${comfort?.score >= 7 ? 'text-emerald-400' : 'text-amber-400'}`}>{comfort?.score}/10</span>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{comfort?.rating}</p>
                {comfort?.notes?.map((note) => <p key={note} className="text-xs text-amber-400 mt-1">{note}</p>)}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              {huntWindows?.map((window) => (
                <div key={window.label} data-testid={`hunting-detail-window-${window.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`p-4 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm">{window.label}</h3>
                    <span className="text-xs font-bold text-amber-400">{window.score}/10</span>
                  </div>
                  <p className="text-sm font-bold">{window.start} - {window.end}</p>
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{window.target}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {safety?.hazards?.length > 0 && (
          <section className="mb-8" data-testid="hunting-safety-detail-section">
            <div className={`p-4 rounded-2xl flex items-start gap-3 ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-orange-50 border border-orange-200'}`}>
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className={`font-semibold text-sm mb-1 ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>Safety Read</h3>
                {safety.hazards.map((hazard) => <p key={hazard} className={`text-sm ${isDarkMode ? 'text-orange-100/75' : 'text-orange-700'}`}>{hazard}</p>)}
              </div>
            </div>
          </section>
        )}

        {/* Sun & Moon */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Sun & Moon</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Golden Hours */}
            <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-4"><Sun className="w-5 h-5 text-orange-400" /><h3 className="font-semibold">Golden Hours</h3></div>
              <div className="space-y-3">
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/15' : 'bg-orange-50 border border-orange-200'}`}>
                  <p className="text-xs text-orange-400 font-semibold mb-1">Prime Morning</p>
                  <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{sun.golden_hours.morning.start} - {sun.golden_hours.morning.end}</p>
                </div>
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/15' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className="text-xs text-amber-400 font-semibold mb-1">Prime Evening</p>
                  <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{sun.golden_hours.evening.start} - {sun.golden_hours.evening.end}</p>
                </div>
                <div className="flex justify-between text-xs mt-2">
                  <span className={isDarkMode ? 'text-white/40' : 'text-gray-400'}>Sunrise: {sun.sunrise}</span>
                  <span className={isDarkMode ? 'text-white/40' : 'text-gray-400'}>Sunset: {sun.sunset}</span>
                </div>
              </div>
            </div>
            {/* Moon & Solunar */}
            <div className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><Moon className="w-5 h-5 text-yellow-300" /><h3 className="font-semibold">Moon & Solunar</h3></div>
                <RatingBadge rating={solunar.rating} />
              </div>
              <div className="flex items-center gap-4 mb-4">
                <MoonIcon phase={moon.phase} illumination={moon.illumination} />
                <div>
                  <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{moon.phase}</p>
                  <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>{moon.illumination}% illuminated</p>
                </div>
              </div>
              <div className="space-y-2">
                {solunar.major_periods.map((p) => (
                  <div key={`major-${p.name}-${p.start}`} className={`flex justify-between text-xs p-2 rounded-lg ${isDarkMode ? 'bg-yellow-500/10' : 'bg-yellow-50'}`}>
                    <span className="text-yellow-400 font-semibold">Major: {p.name}</span>
                    <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>{p.start} - {p.end}</span>
                  </div>
                ))}
                {solunar.minor_periods.map((p) => (
                  <div key={`minor-${p.name}-${p.start}`} className={`flex justify-between text-xs p-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <span className={isDarkMode ? 'text-white/50' : 'text-gray-500'}>Minor: {p.name}</span>
                    <span className={isDarkMode ? 'text-white/40' : 'text-gray-400'}>{p.start} - {p.end}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Game Activity */}
        <section className="mb-8">
          <h2 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${isDarkMode ? 'text-white/40' : 'text-gray-400'}`}>Game Activity Forecast</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: "deer", icon: TreePine, label: "Deer", color: "text-amber-400" },
              { key: "turkey", icon: Bird, label: "Turkey", color: "text-emerald-400" },
              { key: "waterfowl", icon: Droplets, label: "Waterfowl", color: "text-blue-400" },
            ].map(({ key, icon: Icon, label, color }) => {
              const g = ga[key];
              return (
                <div key={key} data-testid={`game-${key}`} className={`p-5 rounded-2xl ${isDarkMode ? 'bg-white/5 border border-white/5' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><Icon className={`w-5 h-5 ${color}`} /><h3 className="font-semibold">{label}</h3></div>
                    <RatingBadge rating={g.rating} />
                  </div>
                  <ScoreRing score={g.score} />
                  <div className="space-y-1 mt-3">
                    {g.factors.map((f) => (
                      <p key={`factor-${f}`} className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{f}</p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
